import { FC, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { help } from 'src/utils/properties.utils';
import {
  EditOrderItemsProps,
  EditOrderProduct,
  EditOrderItem,
  EditOrderItemError,
} from 'src/models/EditOrder.interface';
import {
  FormContainer,
  ContentWrapper,
  ItemsListWrapper,
  Container,
  TitleTypography,
  HeaderRow,
  HeaderColumn,
  HeaderSpacer,
  HeaderColumnText,
  ItemBox,
  ItemsScrollContainer,
  ItemRow,
  FieldColumn,
  StyledAutocomplete,
  StyledAutocompleteTextField,
  StyledTextField,
  ErrorText,
  DeleteButton,
  StyledDeleteIcon,
  AddItemButton,
  StyledAddIcon,
  FooterBox,
  BackButton,
  NextButton,
  AddItemButtonContainer,
} from '../CreateOrder/orderItems.styles';
import { getBasicProducts } from 'src/services/product.service';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';
import { formatNumberWithCommas } from 'src/utils/format.utils';

const OrderItems: FC<EditOrderItemsProps> = ({
  onNext,
  onBack,
  orderId,
  orderData,
}) => {
  const [products, setProducts] = useState<EditOrderProduct[]>([]);
  const [items, setItems] = useState<EditOrderItem[]>([
    { productId: '', quantity: 0, unitPrice: 0 },
  ]);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    fetchProducts();
    loadSavedData();
  }, []);

  useEffect(() => {
    if (orderData && products.length > 0 && !isInitialized) {
      initializeWithOrderData();
    }
  }, [orderData, products, isInitialized]);

  const initializeWithOrderData = () => {
    if (!orderData || !orderData.items) return;

    const savedData = localStorage.getItem(`editOrderStep2_${orderId}`);
    if (savedData) {
      setIsInitialized(true);
      return;
    }

    const mappedItems: EditOrderItem[] = orderData.items.map((item) => {
      const product = products.find((p) => p.name === item.name);
      return {
        productId: product?.id || '',
        productName: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      };
    });

    if (mappedItems.length > 0) {
      setItems(mappedItems);
      formik.setFieldValue('items', mappedItems);
    }
    setIsInitialized(true);
  };

  const fetchProducts = async () => {
    try {
      const data = await getBasicProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const loadSavedData = () => {
    const savedData = localStorage.getItem(`editOrderStep2_${orderId}`);
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setItems(
        parsedData.items || [{ productId: '', quantity: 0, unitPrice: 0 }]
      );
      setIsInitialized(true);
    }
  };

  const validationSchema = Yup.object({
    items: Yup.array().of(
      Yup.object().shape({
        productId: Yup.string().required(help.PRODUCT_REQUIRED),
        quantity: Yup.number()
          .required(help.QUANTITY_REQUIRED)
          .min(1, help.QUANTITY_MIN),
        unitPrice: Yup.number()
          .required(help.VALUE_REQUIRED)
          .moreThan(0, 'Unit price must be greater than 0'),
      })
    ),
  });

  const formik = useFormik({
    initialValues: {
      items: items,
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      // Enhance items with product names before saving
      const itemsWithNames = values.items.map((item) => {
        const product = products.find((p) => p.id === item.productId);
        return {
          ...item,
          unitPrice: Number(item.unitPrice),
          productName: product?.name || item.productName,
        };
      });
      localStorage.setItem(
        `editOrderStep2_${orderId}`,
        JSON.stringify({ items: itemsWithNames })
      );
      onNext();
    },
  });

  const handleAddItem = () => {
    const newItems = [...items, { productId: '', quantity: 0, unitPrice: 0 }];
    setItems(newItems);
    formik.setFieldValue('items', newItems);
  };

  const handleRemoveItem = (index: number) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      formik.setFieldValue('items', newItems);
    }
  };

  const handleProductChange = (index: number, productId: string) => {
    const selectedProduct = products.find((p) => p.id === productId);
    const newItems = [...items];
    newItems[index].productId = productId;
    newItems[index].unitPrice = selectedProduct?.unitPrice || 0;
    setItems(newItems);
    formik.setFieldValue('items', newItems);
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    const newItems = [...items];
    newItems[index].quantity = quantity;
    setItems(newItems);
    formik.setFieldValue('items', newItems);
  };

  const handleUnitPriceChange = (index: number, unitPrice: number | string) => {
    const newItems = [...items];
    newItems[index].unitPrice = unitPrice;
    setItems(newItems);
    formik.setFieldValue('items', newItems);
  };

  return (
    <FormContainer onSubmit={formik.handleSubmit}>
      <ContentWrapper>
        <Container>
          <TitleTypography>Order Items</TitleTypography>
        </Container>

        <ItemsListWrapper>
          <ItemBox itemCount={items.length}>
            <HeaderRow>
              <HeaderColumn>
                <HeaderColumnText>Product</HeaderColumnText>
              </HeaderColumn>
              <HeaderColumn>
                <HeaderColumnText>Quantity</HeaderColumnText>
              </HeaderColumn>
              <HeaderColumn>
                <HeaderColumnText>Unit Price</HeaderColumnText>
              </HeaderColumn>
              <HeaderSpacer />
            </HeaderRow>
            <ItemsScrollContainer>
              {items.map((item, index) => (
                <ItemRow key={index}>
                  <FieldColumn>
                    <StyledAutocomplete
                      options={products.filter(
                        (product) =>
                          item.productId === product.id ||
                          !items.some(
                            (itm, idx) =>
                              idx !== index && itm.productId === product.id
                          )
                      )}
                      getOptionLabel={(option) => option.name}
                      value={
                        products.find((p) => p.id === item.productId) || null
                      }
                      onChange={(event, newValue) => {
                        handleProductChange(index, newValue?.id || '');
                      }}
                      onBlur={formik.handleBlur}
                      ListboxProps={{
                        style: {
                          maxHeight: '350px',
                          overflow: 'auto',
                        },
                      }}
                      renderInput={(params) => (
                        <StyledAutocompleteTextField
                          {...params}
                          name={`items[${index}].productId`}
                          placeholder="Select item"
                          hasValue={!!item.productId}
                          hasError={
                            formik.touched.items?.[index]?.productId &&
                            !!(
                              formik.errors.items?.[index] as EditOrderItemError
                            )?.productId
                          }
                        />
                      )}
                    />
                    {formik.touched.items?.[index]?.productId &&
                      (formik.errors.items?.[index] as EditOrderItemError)
                        ?.productId && (
                        <ErrorText>
                          <ErrorIcon />
                          {
                            (formik.errors.items?.[index] as EditOrderItemError)
                              ?.productId
                          }
                        </ErrorText>
                      )}
                  </FieldColumn>
                  <FieldColumn>
                    <StyledTextField
                      type="text"
                      name={`items[${index}].quantity`}
                      value={
                        item.quantity
                          ? item.quantity.toLocaleString('en-US')
                          : ''
                      }
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, '');
                        if (value === '' || /^\d+$/.test(value)) {
                          handleQuantityChange(
                            index,
                            value === '' ? 0 : Number(value)
                          );
                        }
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.items?.[index]?.quantity &&
                        Boolean(
                          (formik.errors.items?.[index] as EditOrderItemError)
                            ?.quantity
                        )
                      }
                      hasError={
                        formik.touched.items?.[index]?.quantity &&
                        !!(formik.errors.items?.[index] as EditOrderItemError)
                          ?.quantity
                      }
                    />
                    {formik.touched.items?.[index]?.quantity &&
                      (formik.errors.items?.[index] as EditOrderItemError)
                        ?.quantity && (
                        <ErrorText>
                          <ErrorIcon />
                          {
                            (formik.errors.items?.[index] as EditOrderItemError)
                              ?.quantity
                          }
                        </ErrorText>
                      )}
                  </FieldColumn>
                  <FieldColumn>
                    <StyledTextField
                      type="text"
                      name={`items[${index}].unitPrice`}
                      value={
                        item.unitPrice
                          ? formatNumberWithCommas(item.unitPrice)
                          : ''
                      }
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, '');
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          handleUnitPriceChange(
                            index,
                            value === '' ? 0 : value
                          );
                        }
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.items?.[index]?.unitPrice &&
                        Boolean(
                          (formik.errors.items?.[index] as EditOrderItemError)
                            ?.unitPrice
                        )
                      }
                      hasError={
                        formik.touched.items?.[index]?.unitPrice &&
                        !!(formik.errors.items?.[index] as EditOrderItemError)
                          ?.unitPrice
                      }
                    />
                    {formik.touched.items?.[index]?.unitPrice &&
                      (formik.errors.items?.[index] as EditOrderItemError)
                        ?.unitPrice && (
                        <ErrorText>
                          <ErrorIcon />
                          {
                            (formik.errors.items?.[index] as EditOrderItemError)
                              ?.unitPrice
                          }
                        </ErrorText>
                      )}
                  </FieldColumn>
                  {items.length > 1 && (
                    <DeleteButton onClick={() => handleRemoveItem(index)}>
                      <StyledDeleteIcon />
                    </DeleteButton>
                  )}
                </ItemRow>
              ))}
            </ItemsScrollContainer>
          </ItemBox>
          <AddItemButtonContainer>
            <AddItemButton onClick={handleAddItem}>
              <StyledAddIcon />
              Add Item
            </AddItemButton>
          </AddItemButtonContainer>
        </ItemsListWrapper>
      </ContentWrapper>

      <FooterBox>
        <BackButton onClick={onBack}>Back</BackButton>
        <NextButton type="submit">Next</NextButton>
      </FooterBox>
    </FormContainer>
  );
};

export default OrderItems;
